// Monthly Execution Summary shared types/helpers. FOUR tiers per month —
// Planned Revenue (Client) (project × month ₹, hand-entered or derived), Planned
// Expenditure (task schedules × 3M cost), the Execution trio [Target = task
// actuals × PLANNED rates (the earned* fields) | Actual = real consumed cost
// from the daily site captures (actual* fields: material issues × site
// moving-avg price, approved time-log manpower, equipment-log hours ×
// effective-dated ₹/hr) | Deviation = Target − Actual, positive = under cost],
// plus Actual Client Billing (actualQty × Basic Rate) and the carry-over
// waterfall (opening backlog / month variance / closing backlog).
// NOTE the field names below still say target/earned: they are the wire
// contract with the proto and were deliberately NOT renamed with the labels.
// TWO muting signals: isActualized (false after lastActualMonth) governs the
// Target/waterfall side; hasActualCosts (any consumed-cost capture this month)
// governs the Actual/Deviation sub-columns.

export {
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
  computeDeviation,
  fmtINRSigned,
  fmtDevPct,
} from "../ThreeMConsolidation/threeMShared";
export {
  fmtMonth,
  fmtMonthLong,
  fyOf,
  fyOption,
  buildFyOptions,
} from "../MonthlyPnl/pnlShared";
export type { FyOption } from "../MonthlyPnl/pnlShared";

export interface ExecutionMonthColumn {
  month: string; // 'YYYY-MM-01' | 'TOTAL'
  clientPlanAmount: string;
  clientPlanRemarks: string;
  /** True when nothing was entered for the month, so the amount is the
   *  schedules-derived estimate. Always false on the TOTAL column. */
  clientPlanIsDerived?: boolean;
  /** The derived amount, kept even when a manual entry overrides it. */
  clientPlanDerivedAmount?: string;
  targetAmount: string;
  executedAmount: string;
  openingBacklog: string;
  monthVariance: string;   // executed − target, signed
  closingBacklog: string;
  scheduledTaskCount: number;
  executedTaskCount: number;
  isActualized: boolean;
  plannedMaterial: string;
  plannedManpower: string;
  plannedMachinery: string;
  plannedOutflow: string;
  earnedMaterial: string;
  earnedManpower: string;
  earnedMachinery: string;
  earnedOutflow: string;
  materialDeviation: string;
  manpowerDeviation: string;
  machineryDeviation: string;
  outflowDeviation: string;
  /** ACTUAL tier — real consumed cost, NOT valued at planned rates. */
  actualMaterial: string;
  actualManpower: string;
  actualMachinery: string;
  actualOutflow: string;
  /** Σ actualQty × Basic Rate — the billing-basis executed value. */
  actualClientBilling: string;
  /** Cost efficiency: earned − actual. Positive = under cost (opposite
   *  polarity to the volume deviations above). */
  materialCostDeviation: string;
  manpowerCostDeviation: string;
  machineryCostDeviation: string;
  outflowCostDeviation: string;
  /** Any consumed-cost capture exists for the month (governs Actual muting). */
  hasActualCosts: boolean;
}

/** One chainage stretch recorded inside a class's day (metres). */
export interface BillingClassStretch {
  fromChainage: string;
  toChainage: string;
  length: string;
}

/** One day of one class's driver input. stretches is empty for pre-chainage
 *  entries and non-linear classes (typed quantities). */
export interface BillingClassDay {
  date: string;
  qty: string;
  stretches: BillingClassStretch[];
}

/** Class-wise (slot-wise) split of an activity's running meter — the slots
 *  are the capture classes (rock classes on tunnel headings). */
export interface BillingClassRow {
  slotKey: string;
  slotLabel: string;
  uom: string;
  qty: string;
  days: BillingClassDay[];
}

/** One activity in the Actual Client Billing drill. */
export interface BillingActivityRow {
  activityId: number;
  activityName: string;
  /** Site-entered driver qty ("running meters") — '' when the month was billed
   *  via MANUAL task actuals and no daily execution exists. */
  driverQty: string;
  driverUom: string;
  amount: string;
  perUnit: string;
  hasManual: boolean;
  tasks: Array<{
    taskId: number;
    taskSequenceId: string;
    taskName: string;
    qty: string;
    uomName: string;
    rate: string;
    amount: string;
    source: string; // 'MANUAL' | 'DERIVED'
  }>;
  days: Array<{ date: string; driverQty: string; amount: string }>;
  /** Absent on responses older than the class-wise drill. */
  classes?: BillingClassRow[];
}

export interface BillingDrillData {
  month?: string;
  projectName?: string;
  monthTotal?: string;
  activities?: BillingActivityRow[];
}

export interface ExecutionSummaryData {
  projectName?: string;
  scheduleRange?: {
    firstMonth?: string;
    lastMonth?: string;
    lastActualMonth?: string;
  };
  months?: ExecutionMonthColumn[];
  totals?: ExecutionMonthColumn;
}

export interface ExecutionTaskRow {
  phaseId: number;
  phaseName: string;
  activityId: number;
  activityName: string;
  taskId: number;
  taskName: string;
  taskSequenceId: string;
  workQuantity: string;
  workUom: string;
  /** Basic Rate — the task rate net of GST and labour cess. */
  rate: string;
  scheduledQty: string;
  actualQty: string;
  carryQty: string;
  scheduleShare: string;
  actualShare: string;
  targetAmount: string;
  executedAmount: string;
  plannedMaterial: string;
  plannedManpower: string;
  plannedMachinery: string;
  plannedOutflow: string;
  earnedMaterial: string;
  earnedManpower: string;
  earnedMachinery: string;
  earnedOutflow: string;
  outflowDeviation: string;
  actualRemarks: string;
}

export type ExecutionRowKind =
  | "clientPlan"
  | "target"
  | "openingBacklog"
  | "executed"
  | "monthVariance"
  | "closingBacklog"
  | "plannedMaterial"
  | "plannedManpower"
  | "plannedMachinery"
  | "plannedOutflow"
  | "earnedMaterial"
  | "earnedManpower"
  | "earnedMachinery"
  | "earnedOutflow"
  | "outflowDeviation"
  // Actual Client Billing — actualQty × Basic Rate; clicks open the billing
  // drill tab (activity-wise running meters).
  | "actualClientBilling";

export type ExecutionDetailTab =
  | "tasks"
  | "materials"
  | "manpower"
  | "machinery"
  | "billing";

export const kindToTab = (kind: ExecutionRowKind): ExecutionDetailTab => {
  switch (kind) {
    case "plannedMaterial":
    case "earnedMaterial":
      return "materials";
    case "plannedManpower":
    case "earnedManpower":
      return "manpower";
    case "plannedMachinery":
    case "earnedMachinery":
      return "machinery";
    case "actualClientBilling":
      return "billing";
    default:
      return "tasks";
  }
};

// Chart palette — BLUISH theme (user-mandated), validated (dataviz six-checks,
// all pairs, white surface: CVD ≥ 16.9, normal-vision ≥ 17.4, lightness band +
// chroma floor pass). A blue lightness LADDER carries identity — plan-side is
// always the soft sky blue, actual-side the deep primary blue, client plan the
// navy dashed line — so "planned vs actual" reads identically in both charts.
// Backlog keeps the soft-amber EXCEPTION accent (per the design brief: amber
// only for exceptions) — it renders only when a real backlog exists. A strict
// 4-blue set is provably impossible: four in-band blues cannot clear the
// pairwise separation floors (validator-checked). The muted plan bar sits
// below 3:1 on white BY DESIGN (recessive reference); relief = per-mark
// tooltips, legend, and the month tables in the expanded analysis.
export const CHART_COLORS = {
  plan: "#74BBF0",       // Planned Expenditure — soft sky blue, muted reference
  actual: "#2F7FDF",     // Executed Expenditure — deep blue, the primary series
  clientPlan: "#17518D", // Planned Revenue (Client) — navy dashed line, subtle
  backlog: "#C9853A",    // Closing Backlog — soft amber, exception accent only
  grid: "#eef1f6",       // hairline gridlines
  axisLabel: "#94a3b8",  // light axis ink
  axisLine: "#e2e8f0",
  ink: "#022c57",
  positive: "#237804",
  negative: "#cf1322",
};
